import { useState } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { motion } from "framer-motion";
import {
  Brain, Loader2, AlertTriangle, Zap, Copy, Check
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { PageHeader } from "../components/PageHeader";

const RiskPredictor = () => {
  const { token } = useAuth();
  const { t } = useI18n();
  const [prediction, setPrediction] = useState(null);
  const [simulation, setSimulation] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [predicting, setPredicting] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [scenarioText, setScenarioText] = useState("");
  const [copied, setCopied] = useState(false);

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/risk-scenarios/templates`, { headers: { Authorization: `Bearer ${token}` } });
      setTemplates(res.data);
    } catch (error) { console.error(error); }
  };

  useState(() => { fetchTemplates(); }, []);

  const runPrediction = async () => {
    setPredicting(true); setPrediction(null);
    try {
      const res = await axios.get(`${API}/risk-predictor/analyze`, { headers: { Authorization: `Bearer ${token}` } });
      setPrediction(res.data);
      toast.success("Analysis complete");
    } catch (error) { toast.error("Failed"); }
    finally { setPredicting(false); }
  };

  const runSimulation = async (scenario) => {
    const text = scenario || scenarioText;
    if (!text) { toast.error("Enter a scenario"); return; }
    setSimulating(true); setSimulation(null);
    try {
      const res = await axios.post(`${API}/risk-scenarios/simulate?scenario=${encodeURIComponent(text)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSimulation(res.data);
      toast.success("Simulation complete");
    } catch (error) { toast.error("Failed"); }
    finally { setSimulating(false); }
  };

  const copyResult = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true); toast.success("Copied");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="p-6 space-y-6" data-testid="risk-predictor-page">
      <PageHeader
        title={t("predictor.title")}
        description={t("predictor.subtitle")}
      />

      <Tabs defaultValue="predictor" className="space-y-4">
        <TabsList>
          <TabsTrigger value="predictor" data-testid="tab-predictor">Risk Predictor</TabsTrigger>
          <TabsTrigger value="scenarios" data-testid="tab-scenarios">What-If Scenarios</TabsTrigger>
        </TabsList>

        <TabsContent value="predictor" className="space-y-4">
          <Card className="border-border" data-testid="predictor-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2"><Brain className="w-5 h-5 text-purple-500" /> AI Risk Prediction</CardTitle>
                  <CardDescription>Analyze historical data to predict future risks</CardDescription>
                </div>
                <Button onClick={runPrediction} disabled={predicting} data-testid="run-prediction-btn">
                  {predicting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
                  {predicting ? "Analyzing..." : "Run Analysis"}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {predicting ? (
                <div className="flex flex-col items-center py-16">
                  <Loader2 className="w-10 h-10 animate-spin text-purple-500 mb-4" />
                  <p className="text-muted-foreground">AI is analyzing your risk data...</p>
                </div>
              ) : prediction ? (
                <div>
                  <div className="flex justify-end mb-2">
                    <Button variant="outline" size="sm" onClick={() => copyResult(prediction.analysis)}>
                      {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />} Copy
                    </Button>
                  </div>
                  <div className="flex items-center gap-3 mb-4 flex-wrap">
                    <Badge variant="outline">{prediction.data_points?.risks || 0} Risks</Badge>
                    <Badge variant="outline">{prediction.data_points?.incidents || 0} Incidents</Badge>
                    <Badge variant="outline">{prediction.data_points?.active_threats || 0} Active Threats</Badge>
                  </div>
                  <ScrollArea className="h-[calc(100vh-26rem)]">
                    <div className="prose prose-sm dark:prose-invert max-w-none whitespace-pre-wrap leading-relaxed">
                      {prediction.analysis}
                    </div>
                  </ScrollArea>
                </div>
              ) : (
                <div className="text-center py-16">
                  <Brain className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                  <p className="text-muted-foreground">Click "Run Analysis" to generate AI-powered risk predictions</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="scenarios" className="space-y-4">
          {/* Scenario Templates */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {templates.map(t => (
              <Card key={t.id} className="border-border hover:border-primary/50 cursor-pointer transition-colors"
                onClick={() => { setScenarioText(t.description); runSimulation(t.description); }}
                data-testid={`scenario-${t.id}`}>
                <CardContent className="pt-4 pb-4">
                  <p className="text-sm font-medium">{t.name}</p>
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{t.description}</p>
                  <Badge variant="secondary" className="mt-2 text-xs capitalize">{t.category}</Badge>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Custom Scenario */}
          <Card className="border-border" data-testid="custom-scenario-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-orange-500" /> Custom Scenario</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input value={scenarioText} onChange={(e) => setScenarioText(e.target.value)}
                  placeholder="Describe a risk scenario..." className="flex-1"
                  onKeyDown={(e) => e.key === "Enter" && runSimulation()}
                  data-testid="scenario-input" />
                <Button onClick={() => runSimulation()} disabled={simulating} data-testid="run-scenario-btn">
                  {simulating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
                  Simulate
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Simulation Result */}
          {(simulating || simulation) && (
            <Card className="border-border" data-testid="simulation-result">
              <CardHeader>
                <CardTitle className="text-lg">Simulation: {simulation?.scenario || scenarioText}</CardTitle>
              </CardHeader>
              <CardContent>
                {simulating ? (
                  <div className="flex flex-col items-center py-12">
                    <Loader2 className="w-10 h-10 animate-spin text-orange-500 mb-4" />
                    <p className="text-muted-foreground">Running scenario simulation...</p>
                  </div>
                ) : (
                  <ScrollArea className="h-[calc(100vh-30rem)]">
                    <div className="prose prose-sm dark:prose-invert max-w-none whitespace-pre-wrap leading-relaxed">
                      {simulation?.simulation}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default RiskPredictor;
